import {FormProvider, useForm} from 'react-hook-form';
import Dropdown from 'components/Form/Dropdown';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import SectionHeader from 'components/SectionHeader';
import {useBackend} from 'state/backend';
import {c, t} from 'ttag';
import {Option} from 'types';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {
    TeamCreateGroupOrRoleFormFields,
    usePostTeamCreateGroupOrRole,
} from './services';

export type CreateGroupOrRoleBackendData = Backend<{
    availableMembers: Option[];
    kind: 'group' | 'role';
}>;

type CreateGroupOrRoleFormData = FormFields<TeamCreateGroupOrRoleFormFields>;

const CreateGroupOrRole = () => {
    const {
        data: {availableMembers, kind},
    } = useBackend<CreateGroupOrRoleBackendData>();

    const methods = useForm<CreateGroupOrRoleFormData>({
        defaultValues: {
            members: [],
            name: '',
        },
    });
    const {
        formState: {isDirty, isSubmitting},
        handleSubmit,
    } = methods;

    const {mutateAsync} = usePostTeamCreateGroupOrRole(kind);

    const onSubmit = async (formData: CreateGroupOrRoleFormData) => {
        await mutateAsync(formData)
            .then(() => {
                setUrl(window.Urls.team_list_view());
            })
            .catch(onApiFormError(methods));
    };

    const subtitle =
        kind === 'group'
            ? t`Documents and clients owned by a group are only visible to members of the group`
            : t`Assign team members to different functional roles within your company`;
    const title = kind === 'group' ? t`Create Group` : t`Create Role`;
    const inputLabel = kind === 'group' ? c('Groups').t`Name` : t`Role Name`;

    return (
        <FormProvider {...methods}>
            <SectionHeader subtitle={subtitle} title={title} />
            <form onSubmit={handleSubmit(onSubmit)}>
                <SideBySide className="mt-4 max-w-xl">
                    <InputText label={inputLabel} name="name" required={true} />
                    <Dropdown
                        isMultiChoices={true}
                        isSearchable={true}
                        label={t`Members`}
                        name="members"
                        options={availableMembers}
                        type="select"
                    />
                    <FormResult />
                    <FormActions align="left">
                        <SubmitButton disabled={!isDirty || isSubmitting}>
                            {t`Create`}
                        </SubmitButton>
                    </FormActions>
                </SideBySide>
            </form>
        </FormProvider>
    );
};

export default CreateGroupOrRole;
